import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { GraduationCap, ArrowRight, Play, Code2, MessageSquare, Clock, Zap, Users, Shield, Smartphone, Globe, BookOpen, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from "sonner";
import Header from '../components/Header';

export default function Courses() {
  const navigate = useNavigate();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const token = localStorage.getItem('faheem_token');
  
  const [courses, setCourses] = useState([]);
  const [institutions, setInstitutions] = useState({});
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch courses and institutions
        const [coursesRes, institutionsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/courses/`),
          axios.get(`${BACKEND_URL}/api/institutions/`)
        ]);

        const fetchedCourses = coursesRes.data?.courses || [];
        setCourses(fetchedCourses);

        // Create institutions lookup map
        const institutionsList = institutionsRes.data?.institutions || [];
        const institutionsMap = {};
        institutionsList.forEach(inst => {
          institutionsMap[inst.id] = inst;
        });
        setInstitutions(institutionsMap);

        // Fetch user's enrolled courses if logged in
        if (token) {
          try {
            const profileRes = await axios.get(`${BACKEND_URL}/api/supabase-auth/profile?access_token=${token}`);
            setEnrolledCourses(profileRes.data?.enrolled_courses || []);
          } catch (error) {
            console.error('Error fetching profile:', error);
            // Continue without enrolled courses if not logged in or error
            setEnrolledCourses([]);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load courses');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [BACKEND_URL, token]);

  const handleEnroll = async (courseId, title, link) => {
    if (!token) {
        navigate('/login', { state: { from: { pathname: '/courses' } } });
        return;
    }

    setEnrolling(prev => ({ ...prev, [courseId]: true }));

    try {
        await axios.post(`${BACKEND_URL}/api/supabase-auth/enroll?access_token=${token}`, {
            course_id: courseId
        });
        toast.success(`Enrolled in ${title}!`);
        setEnrolledCourses(prev => [...prev, courseId]);
        
        // Navigate to course page
        if (link) {
          navigate(link);
        }
    } catch (error) {
        console.error('Enrollment error:', error);
        if (error.response?.status === 401) {
            toast.error("Session expired. Please log in again.");
            navigate('/login');
        } else {
            toast.error(error.response?.data?.detail || "Enrollment failed. Please try again.");
        }
    } finally {
        setEnrolling(prev => ({ ...prev, [courseId]: false }));
    }
  };

  const handleUnenroll = async (courseId, title) => {
    if (!token) {
        navigate('/login');
        return;
    }

    if (!window.confirm(`Are you sure you want to unenroll from "${title}"?`)) {
      return;
    }

    setEnrolling(prev => ({ ...prev, [courseId]: true }));

    try {
        await axios.post(`${BACKEND_URL}/api/supabase-auth/unenroll?access_token=${token}`, {
            course_id: courseId
        });
        toast.success(`Unenrolled from ${title}`);
        setEnrolledCourses(prev => prev.filter(id => id !== courseId));
    } catch (error) {
        console.error('Unenrollment error:', error);
        toast.error(error.response?.data?.detail || "Unenrollment failed. Please try again.");
    } finally {
        setEnrolling(prev => ({ ...prev, [courseId]: false }));
    }
  };

  // Get color class for icons
  const getIconColorClass = (color) => {
    const colorMap = {
      blue: 'text-blue-500',
      purple: 'text-purple-500',
      green: 'text-green-500',
      orange: 'text-orange-500',
      teal: 'text-teal-500',
      red: 'text-red-500',
      indigo: 'text-indigo-500',
      pink: 'text-pink-500',
      cyan: 'text-cyan-500',
    };
    return colorMap[color?.toLowerCase()] || 'text-blue-500';
  };

  // Get background color class
  const getBgColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-50',
      purple: 'bg-purple-50',
      green: 'bg-green-50',
      orange: 'bg-orange-50',
      teal: 'bg-teal-50',
      red: 'bg-red-50',
      indigo: 'bg-indigo-50',
      pink: 'bg-pink-50',
      cyan: 'bg-cyan-50',
    };
    return colorMap[color?.toLowerCase()] || 'bg-blue-50';
  };

  // Get badge color class
  const getBadgeColorClass = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-700',
      purple: 'bg-purple-100 text-purple-700',
      green: 'bg-green-100 text-green-700',
      orange: 'bg-orange-100 text-orange-700',
      teal: 'bg-teal-100 text-teal-700',
      red: 'bg-red-100 text-red-700',
      indigo: 'bg-indigo-100 text-indigo-700',
      pink: 'bg-pink-100 text-pink-700',
      cyan: 'bg-cyan-100 text-cyan-700',
    };
    return colorMap[color?.toLowerCase()] || 'bg-blue-100 text-blue-700';
  };

  const getLevelBadgeClass = (level) => {
    const levelMap = {
      'beginner': 'bg-blue-100 text-blue-700',
      'intermediate': 'bg-purple-100 text-purple-700',
      'advanced': 'bg-orange-100 text-orange-700',
    };
    return levelMap[level?.toLowerCase()] || 'bg-slate-100 text-slate-700';
  };

  return (
    <div className="min-h-screen bg-white font-sans text-slate-900 selection:bg-blue-100 overflow-y-auto">
      
      <Header />

      {/* Header Section */}
      <section className="pt-16 pb-12 px-6 bg-slate-50 border-b border-slate-100">
        <div className="max-w-4xl mx-auto text-center space-y-6">
          <Badge variant="outline" className="bg-white text-slate-600 border-slate-200 px-4 py-1">Comprehensive Curriculum</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight">Explore Our AI-Powered Courses</h1>
          <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Choose from a variety of specialized tracks designed to take you from beginner to expert, all supported by our intelligent tutoring system.
          </p>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto text-center">
            <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading courses...</p>
          </div>
        </section>
      )}

      {/* Course Sections by Type */}
      {!loading && (
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto space-y-16">
            {/* Group courses by type */}
            {(() => {
              // Group courses by type
              const groupedCourses = courses.reduce((acc, course) => {
                const type = course.type || 'Other';
                if (!acc[type]) {
                  acc[type] = [];
                }
                acc[type].push(course);
                return acc;
              }, {});

              // Get type display info (icon, color, description)
              const getTypeInfo = (type) => {
                const typeMap = {
                  'academic': {
                    label: 'Academic Courses',
                    description: 'Master core subjects with AI-powered tutoring and structured curriculum',
                    icon: GraduationCap,
                    color: 'blue',
                    bgColor: 'bg-blue-50',
                    textColor: 'text-blue-600',
                    borderColor: 'border-blue-200'
                  },
                  'life-skills': {
                    label: 'Life Skills',
                    description: 'Develop essential skills for personal and professional success',
                    icon: Users,
                    color: 'purple',
                    bgColor: 'bg-purple-50',
                    textColor: 'text-purple-600',
                    borderColor: 'border-purple-200'
                  },
                  'service': {
                    label: 'Special Service',
                    description: 'Special intelligent assistants to serve and support others in professional environments',
                    icon: Shield,
                    color: 'cyan',
                    bgColor: 'bg-cyan-50',
                    textColor: 'text-cyan-600',
                    borderColor: 'border-cyan-200'
                  },
                  'skills': {
                    label: 'Skills Development',
                    description: 'Build practical skills for career advancement',
                    icon: Zap,
                    color: 'orange',
                    bgColor: 'bg-orange-50',
                    textColor: 'text-orange-600',
                    borderColor: 'border-orange-200'
                  },
                  'programming': {
                    label: 'Programming & Development',
                    description: 'Learn to code and build software applications',
                    icon: Code2,
                    color: 'green',
                    bgColor: 'bg-green-50',
                    textColor: 'text-green-600',
                    borderColor: 'border-green-200'
                  },
                  'communication': {
                    label: 'Communication',
                    description: 'Enhance your communication and presentation abilities',
                    icon: MessageSquare,
                    color: 'teal',
                    bgColor: 'bg-teal-50',
                    textColor: 'text-teal-600',
                    borderColor: 'border-teal-200'
                  },
                  'technology': {
                    label: 'Technology',
                    description: 'Stay ahead with cutting-edge technology courses',
                    icon: Globe,
                    color: 'indigo',
                    bgColor: 'bg-indigo-50',
                    textColor: 'text-indigo-600',
                    borderColor: 'border-indigo-200'
                  },
                  'Other': {
                    label: 'Other Courses',
                    description: 'Explore additional learning opportunities',
                    icon: BookOpen,
                    color: 'slate',
                    bgColor: 'bg-slate-50',
                    textColor: 'text-slate-600',
                    borderColor: 'border-slate-200'
                  }
                };
                
                // Try exact match first, then case-insensitive
                return typeMap[type] || typeMap[type?.toLowerCase()] || {
                  label: type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, ' '),
                  description: `Explore our ${type.replace(/-/g, ' ')} courses`,
                  icon: BookOpen,
                  color: 'slate',
                  bgColor: 'bg-slate-50',
                  textColor: 'text-slate-600',
                  borderColor: 'border-slate-200'
                };
              };

              const typeOrder = ['academic', 'programming', 'life-skills', 'service', 'skills', 'communication', 'technology', 'Other'];
              const sortedTypes = Object.keys(groupedCourses).sort((a, b) => {
                const aIndex = typeOrder.indexOf(a.toLowerCase()) !== -1 ? typeOrder.indexOf(a.toLowerCase()) : typeOrder.length;
                const bIndex = typeOrder.indexOf(b.toLowerCase()) !== -1 ? typeOrder.indexOf(b.toLowerCase()) : typeOrder.length;
                return aIndex - bIndex;
              });

              return sortedTypes.map((type) => {
                const typeInfo = getTypeInfo(type);
                const TypeIcon = typeInfo.icon;
                const typeCourses = groupedCourses[type];

                return (
                  <div key={type} className="space-y-6">
                    {/* Section Header */}
                    <div className={`flex items-start gap-4 p-6 rounded-2xl ${typeInfo.bgColor} border ${typeInfo.borderColor}`}>
                      <div className={`w-12 h-12 rounded-xl ${typeInfo.bgColor} border ${typeInfo.borderColor} flex items-center justify-center flex-shrink-0`}>
                        <TypeIcon className={`w-6 h-6 ${typeInfo.textColor}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <h2 className="text-2xl font-bold text-slate-900">{typeInfo.label}</h2>
                          <Badge variant="secondary" className="bg-white/80">
                            {typeCourses.length} {typeCourses.length === 1 ? 'Course' : 'Courses'}
                          </Badge>
                        </div>
                        <p className="text-slate-600">{typeInfo.description}</p>
                      </div>
                    </div>

                    {/* Courses Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {typeCourses.map((course) => {
                        const isEnrolled = enrolledCourses.includes(course.id);
                        const isProcessing = enrolling[course.id];
                        const isComingSoon = course.status === 'coming_soon';
                        
                        // Get institutions
                        const offeredBy = course.offered_by;
                        const institutionIds = Array.isArray(offeredBy) ? offeredBy : [offeredBy];
                        const courseInstitutions = institutionIds
                          .map(id => institutions[id])
                          .filter(inst => inst);

                        return (
                          <Card 
                            key={course.id}
                            className={`h-full flex flex-col border-slate-200 hover:shadow-xl transition-all duration-300 overflow-hidden ${isComingSoon ? 'opacity-100 bg-slate-50/50' : ''}`}
                          >
                            <div className={`h-1 ${getBgColorClass(course.color)} flex items-center justify-center transition-colors`}>
                            </div>
                            <CardHeader>
                              <div className="flex justify-between items-start mb-3 gap-2">
                                <Badge className={`${getLevelBadgeClass(course.level)} hover:${getLevelBadgeClass(course.level)} border-none`}>
                                  {course.level || 'All Levels'}
                                </Badge>
                                {isEnrolled && (
                                  <Badge className="bg-green-100 text-green-700 hover:bg-green-100 border-none flex items-center gap-1">
                                    <CheckCircle2 className="w-3 h-3" /> Enrolled
                                  </Badge>
                                )}
                                {isComingSoon && (
                                  <Badge variant="secondary" className="bg-slate-200 text-slate-500">Coming Soon</Badge>
                                )}
                              </div>
                              <CardTitle className="text-xl transition-colors">{course.title}</CardTitle>
                            </CardHeader>
                            <CardContent className="flex-1 space-y-4">
                              <p className="text-sm text-slate-600 leading-relaxed line-clamp-3">
                                {course.description}
                              </p>
                              {course.modules && (
                                <div className="flex items-center gap-2 text-xs text-slate-500">
                                  <Play className="w-3 h-3" />
                                  {course.modules} Modules
                                </div>
                              )}
                              
                              {/* Institution Info */}
                              {courseInstitutions.length > 0 && (
                                <div className="pt-3 border-t border-slate-100">
                                  {courseInstitutions.length === 1 ? (
                                    <div className="flex items-center gap-2">
                                      <img 
                                        src={courseInstitutions[0].logo_url} 
                                        alt={courseInstitutions[0].name}
                                        className="w-5 h-5 object-contain rounded"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                      <span className="text-xs text-slate-600 font-medium">
                                        {courseInstitutions[0].name}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="space-y-1">
                                      <span className="text-[10px] text-slate-400 font-medium uppercase">Offered by:</span>
                                      <div className="flex flex-wrap gap-1.5">
                                        {courseInstitutions.map((institution, idx) => (
                                          <div 
                                            key={idx}
                                            className="flex items-center gap-1 bg-slate-50 px-2 py-0.5 rounded"
                                            title={institution.name}
                                          >
                                            <img 
                                              src={institution.logo_url} 
                                              alt={institution.name}
                                              className="w-3 h-3 object-contain rounded"
                                              onError={(e) => { e.target.style.display = 'none'; }}
                                            />
                                            <span className="text-[10px] text-slate-600 font-medium">
                                              {institution.name.length > 12 
                                                ? institution.name.substring(0, 12) + '...' 
                                                : institution.name}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </CardContent>
                            <CardFooter className="border-t border-slate-100 pt-4 pb-6">
                              {isComingSoon ? (
                                <Button disabled className="w-full bg-slate-200 text-slate-400 cursor-not-allowed hover:bg-slate-200">
                                  Coming Soon
                                </Button>
                              ) : isEnrolled ? (
                                <div className="w-full space-y-2">
                                  <Link to={course.link || `/course/${course.id}`} className="block">
                                    <Button className={`w-full ${getBadgeColorClass(course.color)} hover:opacity-90`}>
                                      Continue Learning <ArrowRight className="w-4 h-4 ml-2" />
                                    </Button>
                                  </Link>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleUnenroll(course.id, course.title)}
                                    disabled={isProcessing}
                                    className="w-full text-xs border-red-200 text-red-600 hover:bg-red-50"
                                  >
                                    {isProcessing ? 'Processing...' : 'Unenroll'}
                                  </Button>
                                </div>
                              ) : (
                                <Button 
                                  className="w-full bg-slate-900 text-white hover:bg-slate-700"
                                  onClick={() => handleEnroll(course.id, course.title, course.link || `/course/${course.id}`)}
                                  disabled={isProcessing}
                                >
                                  {isProcessing ? 'Enrolling...' : 'Enroll Now'} 
                                  <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                              )}
                            </CardFooter>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                );
              });
            })()}

            {/* Empty State */}
            {!loading && courses.length === 0 && (
              <div className="text-center py-16">
                <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-700 mb-2">No Courses Available</h3>
                <p className="text-slate-500">Check back soon for new courses!</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6 text-slate-500 text-sm">
          <div className="flex items-center gap-2">
            <img 
              src="https://customer-assets.emergentagent.com/job_java-mentor-bot/artifacts/o77zmx4s_Gemini_Generated_Image_q1uh1iq1uh1iq1uh.png" 
              alt="FaheemGPT Logo" 
              className="w-8 h-8 rounded-lg object-cover" 
            />
             <span className="font-semibold text-slate-700">FaheemGPT Intelligent Tutoring Platform</span>
          </div>
          <div className="flex gap-8">
            <Link to="/privacy" className="hover:text-slate-900">Privacy</Link>
            <Link to="/terms" className="hover:text-slate-900">Terms</Link>
            <Link to="/support" className="hover:text-slate-900">Support</Link>
          </div>
          <div>
            &copy; 2024 FaheemGPT Inc. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
